/**
 * BadgeCelebration — Full-screen kid-friendly overlay that plays for ~4s when a
 * new badge is earned. Includes confetti-style bursts, a bouncing badge disc,
 * text-to-speech shoutout, and a "Keep going!" button to dismiss.
 *
 * Props:
 *   badge   { id, label, emoji }  — pass label/emoji/id (any combination)
 *   onClose ()                    — required, called when the child dismisses
 */
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BADGE_META = {
  sel_please_badge:  { label: 'Please Please Please', emoji: '🙏', color: 'from-pink-400 to-rose-500' },
  sel_thanks_badge:  { label: 'Thankful Heart',       emoji: '🌟', color: 'from-amber-400 to-orange-500' },
  sel_share_badge:   { label: 'Super Sharer',         emoji: '🤝', color: 'from-emerald-400 to-teal-500' },
  sel_kindness_badge:{ label: 'Kindness Champion',    emoji: '💖', color: 'from-fuchsia-400 to-pink-500' },
  sel_listen_badge:  { label: 'Good Listener',        emoji: '👂', color: 'from-sky-400 to-blue-500' },
  sel_apology_badge: { label: 'Brave Apology',        emoji: '🌈', color: 'from-violet-400 to-indigo-500' },
};

// A confetti dot rendered at a random angle from center
const Confetto = ({ i }) => {
  const angle = (i / 20) * Math.PI * 2;
  const distance = 220 + Math.random() * 120;
  const dx = Math.cos(angle) * distance;
  const dy = Math.sin(angle) * distance;
  const hue = (i * 37) % 360;
  return (
    <motion.span
      className="absolute left-1/2 top-1/2 w-2.5 h-2.5 rounded-sm"
      style={{ background: `hsl(${hue}, 85%, 60%)` }}
      initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
      animate={{ x: dx, y: dy, opacity: 0, rotate: 720 }}
      transition={{ duration: 1.4 + Math.random() * 0.6, ease: 'easeOut', delay: 0.15 }}
    />
  );
};

const BadgeCelebration = ({ badge, onClose }) => {
  const meta = (badge?.id && BADGE_META[badge.id]) || {};
  const label = badge?.label || meta.label || 'New badge!';
  const emoji = badge?.emoji || meta.emoji || '🏆';
  const color = meta.color || 'from-emerald-400 to-teal-500';

  // Text-to-speech shoutout
  useEffect(() => {
    if (!badge) return;
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const u = new window.SpeechSynthesisUtterance(`You earned the ${label} badge! Amazing job!`);
        u.rate = 0.95; u.pitch = 1.15;
        window.speechSynthesis.speak(u);
      }
    } catch (err) { void err; }
    // Auto-dismiss after 6s so nobody gets stuck
    const timer = setTimeout(() => onClose && onClose(), 6000);
    return () => {
      clearTimeout(timer);
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (err) { void err; }
    };
  }, [badge, label, onClose]);

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-slate-900/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="badge-celebration"
        >
          {/* Confetti bursts */}
          <div className="pointer-events-none absolute inset-0">
            {Array.from({ length: 40 }).map((_, i) => <Confetto key={i} i={i} />)}
          </div>

          <motion.div
            className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center"
            initial={{ scale: 0.4, rotate: -8 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14 }}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center"
              aria-label="Close"
              data-testid="badge-celebration-close"
            >
              <X className="w-5 h-5" />
            </button>

            <motion.div
              className="flex items-center justify-center gap-1 text-amber-400 mb-2"
              animate={{ rotate: [0, -8, 8, -8, 0] }}
              transition={{ duration: 1.2, repeat: 2 }}
            >
              <Sparkles className="w-6 h-6" /> <Star className="w-5 h-5 fill-amber-400" /> <Sparkles className="w-6 h-6" />
            </motion.div>

            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Badge earned</p>

            <motion.div
              className={`mx-auto mb-4 w-28 h-28 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}
              animate={{ y: [0, -8, 0], scale: [1, 1.05, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <span className="text-5xl" role="img" aria-label={label}>{emoji}</span>
            </motion.div>

            <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-1" data-testid="badge-celebration-label">{label}</h2>
            <p className="text-sm text-slate-500 mb-5">You&apos;re on a roll! Keep learning. 🎉</p>

            <Button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold h-11 text-base"
              data-testid="badge-celebration-continue"
            >
              Keep going!
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BadgeCelebration;
